/**
 * Iter 67z — WhatsApp click-to-chat button.
 *
 * Renders a small green "WhatsApp" pill that opens wa.me/<phone> in a new tab
 * with an optional pre-filled message. The phone arg can be in any common
 * shape (+33 6 12 ..., 0033..., 06...) — we strip non-digits and require a
 * leading + or country code; otherwise the button hides gracefully.
 *
 * Use everywhere a host or customer phone is shown so the platform encourages
 * direct contact without exposing the raw number to spammers (it lives in the
 * `wa.me` URL but isn't displayed).
 */
import { MessageCircle } from "lucide-react";

function _toWaMe(raw) {
  if (!raw) return null;
  let s = String(raw).trim();
  if (s.toLowerCase().startsWith("whatsapp:")) s = s.slice(9).trim();
  s = s.replace(/[^\d+]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  if (!s.startsWith("+")) return null;
  if (s.length < 8) return null;
  return s.slice(1); // wa.me wants digits only (no '+')
}

export default function WhatsAppButton({
  phone,
  message = "",
  label = "WhatsApp",
  compact = false,
  className = "",
  testId = "whatsapp-btn",
}) {
  const digits = _toWaMe(phone);
  if (!digits) return null;
  const href = `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ""}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition ${
        compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"
      } ${className}`}
      data-testid={testId}
      title={`Open WhatsApp chat with ${phone}`}
    >
      <MessageCircle className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {label}
    </a>
  );
}
